import {
  d1GetAdminUsers,
  d1UpdateUserRole,
  d1UpdateUserStatus,
  d1GetAdminNotes,
  d1GetPendingAdminNotes,
  d1ReviewNote,
  d1ModerateNote,
  d1AdminDeleteNote,
  d1GetAdminStats,
} from "../services/d1Client";
import { getLocalNotes } from "./notesService";

/**
 * Fetch all users for Admin Panel
 */
export const getAllUsers = async () => {
  try {
    const users = await d1GetAdminUsers();
    return users || [];
  } catch (e) {
    console.warn("Failed to fetch all users from Cloudflare D1:", e);
    return [];
  }
};

/**
 * Update user status (active / disabled)
 */
export const updateUserStatus = async (uid, status) => {
  try {
    await d1UpdateUserStatus(uid, status);
    return true;
  } catch (e) {
    console.error("Failed to update user status in Cloudflare D1", e);
    return false;
  }
};

/**
 * Update user role (admin / user)
 */
export const updateUserRole = async (uid, role) => {
  try {
    await d1UpdateUserRole(uid, role);
    return true;
  } catch (e) {
    console.error("Failed to update user role in Cloudflare D1", e);
    return false;
  }
};

/**
 * Fetch all notes for moderation (both public & private)
 */
export const getAllNotesForAdmin = async () => {
  try {
    const notes = await d1GetAdminNotes();
    const local = getLocalNotes();
    return [...(notes || []), ...local];
  } catch (e) {
    console.warn("Failed to fetch notes for admin from Cloudflare D1:", e);
    return getLocalNotes();
  }
};

/**
 * Fetch pending notes awaiting admin review
 */
export const getPendingNotesForAdmin = async () => {
  try {
    if (typeof d1GetPendingAdminNotes === "function") {
      const pending = await d1GetPendingAdminNotes();
      return pending || [];
    }
    const all = await getAllNotesForAdmin();
    return all.filter((n) => n.reviewStatus === "pending");
  } catch (e) {
    console.warn("Failed to fetch pending notes for admin:", e);
    return [];
  }
};

/**
 * Review a pending note (approve or reject)
 */
export const reviewNote = async (noteId, { action, reason = "" } = {}) => {
  try {
    await d1ReviewNote(noteId, { action, reason });
    window.dispatchEvent(new Event("bible_notes_changed"));
    window.dispatchEvent(new Event("bible_shared_notes_changed"));
    return true;
  } catch (e) {
    console.error("Failed to review note in Cloudflare D1", e);
    return false;
  }
};

/**
 * Moderate a shared note (unshare or set featured)
 */
export const moderateNote = async (noteId, updates) => {
  try {
    await d1ModerateNote(noteId, updates);
    window.dispatchEvent(new Event("bible_notes_changed"));
    return true;
  } catch (e) {
    console.error("Failed to moderate note in Cloudflare D1", e);
    return false;
  }
};

/**
 * Admin delete note
 */
export const adminDeleteNote = async (noteId) => {
  try {
    await d1AdminDeleteNote(noteId);
    window.dispatchEvent(new Event("bible_notes_changed"));
    return true;
  } catch (e) {
    console.error("Failed to delete note from Cloudflare D1", e);
    return false;
  }
};

/**
 * Fetch overview statistics
 */
export const getAdminStats = async () => {
  try {
    const stats = await d1GetAdminStats();
    if (stats) return stats;
  } catch (e) {
    console.warn("Failed to get admin stats directly from D1, falling back to calculation", e);
  }

  const users = await getAllUsers();
  const notes = await getAllNotesForAdmin();
  const sharedNotes = notes.filter((n) => Boolean(n.isPublic));

  return {
    totalUsers: users.length,
    totalNotes: notes.length,
    totalSharedNotes: sharedNotes.length,
    activeUsers: users.filter((u) => u.status !== "disabled").length,
  };
};
